import React, { useEffect, useRef, useState } from 'react';
import { publicImage, sizeAsLoaded } from '../../content/imageUrl';
import styles from './styles.module.scss';

// How wide a picture is shown in each layout, for the browser to choose the
// right copy. These follow the stylesheets of the pages that use them.
export const SIZES = {
  // Work page and project galleries: one column, three from 1000 px, four from 2000 px.
  grid: '(min-width: 2000px) calc((100vw - 40px) / 4), (min-width: 1000px) calc((100vw - 32px) / 3), 100vw',
  // Fine-art list: the same columns inside 12% side padding with 120 px gaps.
  fineArtList: '(min-width: 2000px) calc((76vw - 360px) / 4), (min-width: 1000px) calc((76vw - 240px) / 3), (max-width: 768px) 88vw, 100vw',
  // Fine-art page: a third or half of the row from 768 px, the full row below.
  fineArtTall: '(min-width: 768px) calc((100vw - 96px) / 3), calc(100vw - 32px)',
  fineArtWide: '(min-width: 768px) calc((100vw - 96px) / 2), calc(100vw - 32px)',
  // The cover in the admin preview: a card at most 420 px wide.
  previewCover: '(min-width: 480px) 420px, calc(100vw - 32px)',
};

// One picture on a public page, from an image reference. It asks for a copy
// that fits where it is shown, reserves its space before it arrives, and
// loads late unless it is `priority` (likely to be visible at once).
// With `fade` it stays invisible until it has loaded, then fades in.
// `quality` is 'display' (the default) or 'detail'.
const Picture = React.forwardRef(function Picture({ image, sizes, quality, priority = false, fade = false, alt = '', className = 'img-fluid', onLoad, ...rest }, ref) {
  const [loaded, setLoaded] = useState(false);
  const [turned, setTurned] = useState(false);
  const own = useRef(null);
  const picture = publicImage(image, quality);

  // The picture has arrived: show it, and if it is upright where the recorded
  // size lies on its side, lay it out the way it really is.
  const arrived = (img) => {
    setLoaded(true);
    if (picture && picture.width) setTurned(sizeAsLoaded([picture.width, picture.height], img.naturalWidth, img.naturalHeight)[0] !== picture.width);
  };

  // Already there when the page was drawn (from the cache): no load event follows.
  useEffect(() => {
    if (own.current?.complete && own.current.naturalWidth > 0) arrived(own.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!picture) return null;
  const hints = priority ? { loading: 'eager', fetchpriority: 'high' } : { loading: 'lazy' };
  const fading = fade ? ` ${loaded ? styles.visible : styles.hidden}` : '';
  return (
    // src comes last: some browsers start loading as soon as they see it.
    <img
      ref={(node) => {
        own.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={`${className}${fading}`}
      alt={alt}
      decoding="async"
      {...hints}
      width={turned ? picture.height : picture.width}
      height={turned ? picture.width : picture.height}
      sizes={picture.srcSet ? sizes : undefined}
      srcSet={picture.srcSet}
      {...rest}
      onLoad={(event) => {
        arrived(event.currentTarget);
        if (onLoad) onLoad(event);
      }}
      src={picture.src}
    />
  );
});

export default Picture;
